<script>
  import { customersApi } from '../stores/api.js';
  import { activeCompanyId } from '../stores/company.js';
  import { push } from 'svelte-spa-router';
  import { t } from '../i18n/index.js';
  import { Search, X, AlertTriangle, UserPlus } from '../lib/icons/index.js';
  import { formatSede, isSedeEmpty, isSedeIncomplete } from '../lib/customer-sede.js';

  export let value = null;
  export let onSelect = () => {};
  /**
   * Restrict the suggestions to one role: 'cliente' when writing an invoice,
   * 'fornitore' when writing an autofattura. Records marked 'entrambi' appear
   * under both. Leave null to search the whole anagrafica.
   * @type {'cliente'|'fornitore'|'entrambi'|null}
   */
  export let kind = null;

  const MIN_CHARS = 2;

  let query = '';
  let results = [];
  let showDropdown = false;
  let loading = false;
  let searchFailed = false;
  let inputEl;

  // Three outcomes, three visible answers. Before, all three looked identical:
  // the panel simply stayed shut on no matches AND on a failed request, so
  // "questo cliente non esiste" and "la ricerca non ha funzionato" were the
  // same non-event, and the `loading` flag was set but never rendered.
  async function handleInput() {
    if (query.length < MIN_CHARS) {
      results = [];
      searchFailed = false;
      loading = false;
      showDropdown = false;
      return;
    }
    loading = true;
    searchFailed = false;
    showDropdown = true;
    const attempted = query;
    try {
      const found = await customersApi.search($activeCompanyId, query, { kind });
      // A slow answer for a query the user has already changed must not
      // overwrite the results of the query they are actually typing.
      if (attempted !== query) return;
      results = Array.isArray(found) ? found : [];
    } catch {
      if (attempted !== query) return;
      results = [];
      searchFailed = true;
    } finally {
      if (attempted === query) loading = false;
    }
  }

  function goToCustomers() {
    showDropdown = false;
    push('/customers');
  }

  function selectCustomer(customer) {
    value = customer;
    query = customer.ragioneSociale || `${customer.nome || ''} ${customer.cognome || ''}`.trim();
    showDropdown = false;
    onSelect(customer);
  }

  function clear() {
    value = null;
    query = '';
    results = [];
    showDropdown = false;
    onSelect(null);
  }

  function displayName(c) {
    if (c.tipo === 'persona_fisica') return `${c.nome} ${c.cognome}`;
    return c.ragioneSociale || '-';
  }
</script>

<div class="relative">
  <div class="flex">
    <div class="relative flex-1">
      <Search size={14} class="absolute left-3 top-1/2 -translate-y-1/2" style="color: var(--color-text-muted);" />
      <input
        bind:this={inputEl}
        bind:value={query}
        on:input={handleInput}
        on:focus={() => { if (query.length >= MIN_CHARS) showDropdown = true; }}
        placeholder={$t('customers.autocompletePlaceholder')}
        aria-label={$t('customers.autocompletePlaceholder')}
        aria-describedby="customer-autocomplete-hint"
        class="input pl-9 pr-8"
      />
      {#if value}
        <!-- type="button": this component sits inside the <form> of
             InvoiceCreate and AutofatturaCreate, where a bare <button> defaults
             to type="submit" and would send a half-filled invoice on the click
             that was only meant to clear the field. -->
        <button type="button" on:click={clear} class="absolute right-2 top-1/2 -translate-y-1/2 hover:text-danger" style="color: var(--color-text-muted);"
          aria-label={$t('customers.autocomplete.clear')}>
          <X size={14} />
        </button>
      {/if}
    </div>
  </div>

  {#if showDropdown}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto border-w"
      role="status" aria-live="polite"
      style="background-color: var(--color-surface-dark); border-color: var(--color-tint-medium);">
      {#if loading}
        <div class="px-3 py-2 text-sm" style="color: var(--color-text-muted);">
          {$t('customers.autocomplete.searching')}
        </div>
      {:else if searchFailed}
        <div class="px-3 py-2 text-sm flex items-start gap-2 text-danger">
          <AlertTriangle size={14} class="flex-shrink-0 mt-0.5" />
          <span>{$t('customers.autocomplete.searchFailed')}</span>
        </div>
      {:else if results.length === 0}
        <div class="px-3 py-2 text-sm" style="color: var(--color-text-muted);">
          <p>{$t('customers.autocomplete.noResults')}</p>
          <button type="button" class="mt-1 inline-flex items-center gap-1 eyebrow hover:underline"
            style="color: var(--color-primary);" on:click={goToCustomers}>
            <UserPlus size={12} />{$t('customers.autocomplete.createCta')}
          </button>
        </div>
      {:else}
        {#each results as customer}
          <button
            type="button"
            class="w-full text-left px-3 py-2 text-sm font-mono hover:bg-primary-500/10 transition-colors"
            style="color: var(--color-text-body);"
            on:click={() => selectCustomer(customer)}
          >
            <div class="font-bold">{displayName(customer)}</div>
            <div class="text-xs" style="color: var(--color-text-muted);">
              {customer.partitaIva || ''} {customer.codiceDestinatario ? `· ${customer.codiceDestinatario}` : ''}
            </div>
          </button>
        {/each}
      {/if}
    </div>
  {/if}

  <!-- Says why nothing happens under two characters, and what the codes in the
       list are, before the user concludes the search is broken. -->
  <p id="customer-autocomplete-hint" class="mt-1 text-xs" style="color: var(--color-text-muted);">
    {$t('customers.autocomplete.hint')}
  </p>

  {#if value}
    <div class="mt-1 text-xs font-mono" style="color: var(--color-primary);">
      {$t('customers.selected')}: {displayName(value)} · {$t('customers.fields.partitaIvaShort')}: {value.partitaIva || '-'} · {$t('customers.fields.codiceDestinatarioShort')}: {value.codiceDestinatario}
    </div>
    {#if formatSede(value.sede)}
      <div class="mt-1 text-xs" style="color: var(--color-text-muted);">
        {$t('customers.sede.selected', { sede: formatSede(value.sede) })}
      </div>
    {/if}
    <!-- Not a block: the document can be prepared, and the anagrafica
         completed before it is issued. -->
    {#if isSedeIncomplete(value.sede)}
      <div class="alert alert-warning mt-2" role="status">
        <AlertTriangle size={16} class="alert-mark mt-0.5" aria-hidden="true" />
        <div class="min-w-0">
          <p class="alert-title">
            {isSedeEmpty(value.sede)
              ? $t('customers.sede.draftWarning.titleMissing', { nome: displayName(value) })
              : $t('customers.sede.draftWarning.titleIncomplete', { nome: displayName(value) })}
          </p>
          <p class="alert-message">{$t('customers.sede.draftWarning.body')}</p>
          <button type="button" class="mt-1 inline-flex items-center gap-1 text-xs hover:underline"
            style="color: var(--color-primary);" on:click={goToCustomers}>
            {$t('customers.sede.draftWarning.action')}
          </button>
        </div>
      </div>
    {/if}
  {/if}
</div>
